import React, { useState } from 'react';
import { Button } from '../button';
import { HistoryOutlined } from '@ant-design/icons';
import { CmsUserHistory, PaginatedList } from '../../types';
import { CmsUserHistoryModal } from './cms-user-history-modal';
import { formatDate } from './utils';
import style from './style.module.scss';

function getHistoryLines(cmsUserHistory: CmsUserHistory[]): string[] {
    const lastUpdated =
        cmsUserHistory.length > 0
            ? cmsUserHistory.reduce((a, b) =>
                  a.createdAt > b.createdAt ? a : b
              )
            : null;

    const createdBy =
        cmsUserHistory.find((creator) => creator.action === 'create') || null;

    const lines: string[] = [];
    if (createdBy) {
        lines.push(
            `Created by ${createdBy.email} on ${formatDate(
                createdBy.createdAt
            )}.`
        );
    }
    if (lastUpdated) {
        lines.push(
            `Last modified by ${lastUpdated.email} on ${formatDate(
                lastUpdated.createdAt
            )}.`
        );
    }
    return lines;
}

export interface CmsUserHistoryBarProps {
    cmsUserHistory?: CmsUserHistory[];
    fetchHistory?: (
        page: number,
        perPage: number,
        action?: string
    ) => Promise<void>;
    historyData?: PaginatedList<CmsUserHistory> | null;
    isFetching?: boolean;
    actionFilter?: string;
    onActionFilterChange?: (action: string | undefined) => void;
    availableActions?: readonly string[];
}

export function CmsUserHistoryBar({
    cmsUserHistory,
    fetchHistory,
    historyData,
    isFetching = false,
    actionFilter,
    onActionFilterChange,
    availableActions,
}: CmsUserHistoryBarProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const displayHistory = historyData?.results ?? cmsUserHistory ?? [];
    const lines = getHistoryLines(displayHistory);

    const hasHistoryData =
        (historyData && historyData.total > 0) ||
        (cmsUserHistory && cmsUserHistory.length > 0) ||
        (historyData && historyData.results && historyData.results.length > 0);

    const shouldShowBar =
        lines.length > 0 || isFetching || hasHistoryData || availableActions;

    if (!shouldShowBar) {
        return null;
    }

    return (
        <>
            <div className={style.version_history_bar}>
                <div className={`${style.text_section} footnote_description`}>
                    {lines.map((text, index) => (
                        <React.Fragment key={index}>
                            {text}
                            {index < lines.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </div>
                <Button variant="tertiary" onClick={() => setIsModalOpen(true)}>
                    Show version history
                    <HistoryOutlined />
                </Button>
            </div>
            <CmsUserHistoryModal
                open={isModalOpen}
                cmsUserHistory={cmsUserHistory}
                fetchHistory={fetchHistory}
                historyData={historyData}
                isFetching={isFetching}
                actionFilter={actionFilter}
                onActionFilterChange={onActionFilterChange}
                availableActions={availableActions}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
}
